import dayjs, { Dayjs } from 'dayjs';
import React, {
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { Loader } from '../../../components/Loader/Loader';
import './CapacityManagement.scss';
import {
  CapacityInfoData,
  RegionCapacity,
  StoreCapacity,
} from '../../../types/capacityTypes';
import { fetchCapacityInfo } from '../../../fetch/fetchCapacity';
import { CapacityRegionSection } from './CapacityRegionSection/CapacityRegionSection';
import { CapacityStoreSection } from './CapacityStoreSection/CapacityStoreSection';
import { CalendarInputComponent } from '../../../ReusableComponents/CalendarInput/CalendarInputComponent';
import { useDeepCompareMemo } from 'use-deep-compare';
import {
  RegionOrderCount,
  RegionZoneIntervalOrderCount,
} from '../../../types/statsTypes';
import {
  fetchOrderCountRegion,
  fetchOrdersToSortCount,
} from '../../../fetch/fetchStats';

const CapacityManagement: FunctionComponent = memo(() => {
  const [loading, setLoading] = useState<boolean>(true);
  const [capacityState, setCapacityState] = useState<CapacityInfoData>({
    regions: [],
    stores: [],
  });
  const [orderCountRegion, setOrderCountRegion] = useState<
    RegionZoneIntervalOrderCount[]
  >([]);
  const [ordersToSortCount, setOrdersToSortCount] = useState<
    RegionOrderCount[]
  >([]);
  const [deliveryDate, setDeliveryDate] = useState<Dayjs>(dayjs());
  const allowEditing = !deliveryDate.isBefore(dayjs(), 'day');

  const changeDate = (newDeliveryDate: Dayjs): void => {
    setLoading(true);
    setDeliveryDate(newDeliveryDate);
  };

  const setDate = useCallback(
    (selectedDate: string): void => changeDate(dayjs(selectedDate)),
    []
  );

  const fetchCapacityInfoCallback = useCallback(async () => {
    const capacityInfo = await fetchCapacityInfo(deliveryDate);
    capacityInfo.stores = [...capacityInfo.stores].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    setCapacityState(capacityInfo);
  }, [deliveryDate]);

  const fetchAllData = useCallback(async () => {
    await fetchCapacityInfoCallback();
    await fetchOrdersToSortCount(deliveryDate).then(setOrdersToSortCount);
    await fetchOrderCountRegion(deliveryDate.format('YYYY-MM-DD')).then(
      setOrderCountRegion
    );
  }, [fetchCapacityInfoCallback, deliveryDate]);

  useEffect(() => {
    setLoading(true);
    fetchAllData().then(() => setLoading(false));
  }, [fetchAllData]);

  const memoizedStoreCapacity = useDeepCompareMemo(
    (): StoreCapacity[] => capacityState?.stores,
    [capacityState && capacityState.stores]
  );

  const memoizedRegionCapacity = useDeepCompareMemo(
    (): RegionCapacity[] => capacityState?.regions,
    [capacityState && capacityState.regions]
  );

  return (
    <>
      <Helmet>
        <title>Dashboard - Capacity Management</title>
      </Helmet>
      <div className="capacity-info">
        <div className="capacity-info__container">
          <div className="capacity-info__tools-header">
            <h3>CAPACITY MANAGEMENT</h3>
            <CalendarInputComponent
              className="capacity-date-picker"
              dateFormatToShow="DD MMMM"
              date={deliveryDate}
              setDate={setDate}
              variant="text"
            />
          </div>
          {loading ? (
            <div className="capacity-info__loader-container">
              <Loader size="xxl" type="spinner" />
            </div>
          ) : (
            <>
              <CapacityRegionSection
                regionCapacity={memoizedRegionCapacity}
                orderCountRegion={orderCountRegion}
                ordersToSortCount={ordersToSortCount}
                updateRegionalCapacity={fetchCapacityInfoCallback}
                allowEditing={allowEditing}
                deliveryDate={deliveryDate}
              />
              <CapacityStoreSection
                storesCapacity={memoizedStoreCapacity}
                updateStoresCapacity={fetchCapacityInfoCallback}
                allowEditing={allowEditing}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
});

CapacityManagement.displayName = 'CapacityManagement';
CapacityManagement.whyDidYouRender = true;
export { CapacityManagement };
